/**
 * Billing / Stripe subscription types (Standard / Plus).
 * Align with GET /api/v1/billing/status and checkout/portal responses.
 */

export type BillingPlan = "standard" | "plus" | "pro";
/** The plan in effect: Free unless the subscription is active / trialing. */
export type EffectivePlan = "free" | BillingPlan;
export type BillingCheckoutInterval = "month" | "year" | "3month";

export const BILLING_PLAN_LABEL: Record<BillingPlan, string> = {
  standard: "Standard",
  plus: "Plus",
  pro: "Pro",
};

/** Areas a plan opens (GET /billing/status → entitlements.features). Free has none. */
export type PlanFeature = "statistics" | "reports" | "workspace" | "launchpad" | "pine";

/** A plan's limits, from the backend (docs/PLANS_AND_CREDITS.md). Never hard-code these. */
export interface PlanEntitlements {
  plan: EffectivePlan;
  display_name: string;
  paid: boolean;
  features: PlanFeature[];
  reports_per_week: number;
  sims_per_report: number;
  broker_accounts_max: number | null;
  import_history_days: number | null;
  ai_credits_per_period: number;
  ai_prompts_per_week: number | null;
  ai_turn_cap_credits: number;
  lab_backtests: number;
  lab_backtests_window: "week" | "month";
  lab_min_candle_minutes: number;
  lab_history_days: { up_to_15m: number; "30m_to_8h": number; daily_and_up: number } | null;
  markets_test_max: number;
  deploy_running_max: number;
  deploy_live_max: number;
  deploy_auto: boolean;
  deploy_pine: boolean;
  deploy_per_strategy_max: number;
  pack_bonus_pct: number;
}

/** TES allowance: Free counts prompts per week, paid plans spend credits (1 credit ≈ $0.01 of AI). */
export interface AiBalance {
  mode: "prompts" | "credits";
  plan: EffectivePlan;
  turn_cap_credits: number;
  prompts_per_week?: number;
  prompts_used?: number;
  prompts_remaining?: number;
  plan_credits: number;
  plan_used: number;
  plan_remaining: number;
  packs_remaining: number;
  packs_next_expiry?: string | null;
  total_remaining: number;
  period_start?: string;
  period_end?: string;
  admin_bypass?: boolean;
}

export interface BacktestAllowance {
  used: number;
  cap: number;
  remaining: number;
  allowed: boolean;
  window: "week" | "month";
  admin_bypass?: boolean;
}

export interface CreditPack {
  id: "pack_10" | "pack_25" | "pack_50" | "pack_100" | string;
  price_usd: number;
  base_credits: number;
  /** Credits this user gets now (first-pack or plan bonus applied). */
  credits: number;
}

export interface PackOffer {
  available: boolean;
  first_pack: boolean;
  bonus_pct: number;
  packs: CreditPack[];
}

export const STANDARD_REPORTS_PER_WEEK = 10;
export const PLUS_REPORTS_PER_WEEK = 40;
export const STANDARD_SIMS_PER_REPORT = 5;
export const PLUS_SIMS_PER_REPORT = 16;

/** Legacy Stripe metadata `tier1_dashboard` is Plus. */
export function normalizeBillingPlan(raw: unknown): BillingPlan | null {
  if (raw == null || raw === "") return null;
  const s = String(raw).toLowerCase().trim();
  if (s === "plus" || s === "tier1" || s === "tier1_dashboard" || s === "1") return "plus";
  if (s === "standard") return "standard";
  if (s === "pro") return "pro";
  return null;
}

function pickBillingBool(...vals: unknown[]): boolean | undefined {
  for (const v of vals) {
    if (v === null || v === undefined) continue;
    if (typeof v === "boolean") return v;
    if (typeof v === "string") {
      const s = v.toLowerCase().trim();
      if (s === "true" || s === "1") return true;
      if (s === "false" || s === "0") return false;
    }
  }
  return undefined;
}

function pickBillingInt(...vals: unknown[]): number | undefined {
  for (const v of vals) {
    if (v === null || v === undefined || v === "") continue;
    const n = typeof v === "number" ? v : Number(v);
    if (Number.isFinite(n)) return Math.floor(n);
  }
  return undefined;
}

/** Raw status strings from Stripe / backend */
export type BillingSubscriptionStatus =
  | "trialing"
  | "active"
  | "past_due"
  | "canceled"
  | "unpaid"
  | "incomplete"
  | "incomplete_expired"
  | "paused"
  | string;

export interface BillingStatusResponse {
  /** Backend may send null when there is no Stripe subscription yet */
  status: BillingSubscriptionStatus | null;
  trial_end?: string | null;
  current_period_end?: string | null;
  cancel_at_period_end?: boolean;
  /** Normalized plan when subscribed (`standard` | `plus`). Legacy `tier1_dashboard` → plus. */
  tier?: BillingPlan | string | null;
  plan?: BillingPlan | null;
  reports_per_week?: number | null;
  sims_per_report?: number | null;
  reports_used_this_week?: number | null;
  /** Server-controlled 3-month offer campaign; do not hardcode end dates. */
  three_month_offer_available?: boolean;
  /** Optional Stripe customer/payment-method details for billing UI card */
  customer_name?: string | null;
  card_brand?: string | null;
  card_last4?: string | null;
  card_exp_month?: number | null;
  card_exp_year?: number | null;
  /** The plan in effect and its limits / usage (see PlanEntitlements). */
  effective_plan?: EffectivePlan;
  entitlements?: PlanEntitlements | null;
  ai?: AiBalance | null;
  backtests?: BacktestAllowance | null;
  packs?: PackOffer | null;
  /** Paid add-ons on the subscription, e.g. { live_slot: 2, paper_slots: 1 }. */
  addons?: Record<string, number> | null;
  /** "month" | "year" from the subscription's current period; null without one. */
  billing_interval?: "month" | "year" | null;
}

function pickBillingString(...vals: unknown[]): string | undefined {
  for (const v of vals) {
    if (v === null || v === undefined) continue;
    if (typeof v === "string") return v;
    if (typeof v === "number" || typeof v === "boolean") return String(v);
  }
  return undefined;
}

function isHttpishStatusCode(val: unknown): boolean {
  if (typeof val === "number") return Number.isFinite(val);
  if (typeof val === "string" && /^\d{3}$/.test(val.trim())) return true;
  return false;
}

/** Skip HTTP codes and envelope flags so `subscription_status: "active"` is not shadowed by `status: 402`. */
function coerceSubscriptionStatus(val: unknown): BillingSubscriptionStatus | null {
  if (val == null || isHttpishStatusCode(val)) return null;
  if (typeof val !== "string") return null;
  const s = val.trim();
  if (!s) return null;
  const lower = s.toLowerCase();
  if (lower === "error" || lower === "ok" || lower === "success") return null;
  return s;
}

function pickSubscriptionStatus(...vals: unknown[]): BillingSubscriptionStatus | null {
  for (const v of vals) {
    const s = coerceSubscriptionStatus(v);
    if (s) return s;
  }
  return null;
}

/**
 * Map common backend shapes (snake_case, nested `data`, alternate keys) onto BillingStatusResponse.
 */
export function normalizeBillingStatusResponse(raw: unknown): BillingStatusResponse {
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const nested =
    o.data && typeof o.data === "object" ? (o.data as Record<string, unknown>) : {};
  const subscription =
    (o.subscription && typeof o.subscription === "object" ? (o.subscription as Record<string, unknown>) : null) ??
    (nested.subscription && typeof nested.subscription === "object"
      ? (nested.subscription as Record<string, unknown>)
      : null);
  const customer =
    (o.customer && typeof o.customer === "object" ? (o.customer as Record<string, unknown>) : null) ??
    (nested.customer && typeof nested.customer === "object" ? (nested.customer as Record<string, unknown>) : null) ??
    (subscription?.customer && typeof subscription.customer === "object"
      ? (subscription.customer as Record<string, unknown>)
      : null);
  const paymentMethod =
    (o.payment_method && typeof o.payment_method === "object" ? (o.payment_method as Record<string, unknown>) : null) ??
    (nested.payment_method && typeof nested.payment_method === "object"
      ? (nested.payment_method as Record<string, unknown>)
      : null) ??
    (o.default_payment_method && typeof o.default_payment_method === "object"
      ? (o.default_payment_method as Record<string, unknown>)
      : null) ??
    (nested.default_payment_method && typeof nested.default_payment_method === "object"
      ? (nested.default_payment_method as Record<string, unknown>)
      : null) ??
    (subscription?.default_payment_method &&
    typeof subscription.default_payment_method === "object"
      ? (subscription.default_payment_method as Record<string, unknown>)
      : null);
  const pmCard =
    paymentMethod?.card && typeof paymentMethod.card === "object"
      ? (paymentMethod.card as Record<string, unknown>)
      : null;

  let status = pickSubscriptionStatus(
    o.status,
    nested.status,
    subscription?.status,
    o.subscription_status,
    o.subscriptionStatus,
    nested.subscription_status,
    nested.subscriptionStatus,
    subscription?.subscription_status
  );

  const trial_end = pickBillingString(
    o.trial_end,
    nested.trial_end,
    o.trialEnd,
    nested.trialEnd
  );
  const current_period_end = pickBillingString(
    o.current_period_end,
    nested.current_period_end,
    o.currentPeriodEnd,
    nested.currentPeriodEnd
  );
  const cancel_at_period_end = pickBillingBool(
    o.cancel_at_period_end,
    nested.cancel_at_period_end,
    o.cancelAtPeriodEnd,
    nested.cancelAtPeriodEnd
  );
  const plan =
    normalizeBillingPlan(o.plan ?? nested.plan ?? o.tier ?? nested.tier) ??
    normalizeBillingPlan(subscription?.plan ?? subscription?.tier);
  const tier = plan ?? normalizeBillingPlan(o.tier ?? nested.tier);
  const reports_per_week = pickBillingInt(
    o.reports_per_week,
    nested.reports_per_week,
    o.reportsPerWeek,
    nested.reportsPerWeek
  );
  const sims_per_report = pickBillingInt(
    o.sims_per_report,
    nested.sims_per_report,
    o.simsPerReport,
    nested.simsPerReport
  );
  const reports_used_this_week = pickBillingInt(
    o.reports_used_this_week,
    nested.reports_used_this_week,
    o.reportsUsedThisWeek,
    nested.reportsUsedThisWeek
  );
  const three_month_offer_available = pickBillingBool(
    o.three_month_offer_available,
    nested.three_month_offer_available,
    o.threeMonthOfferAvailable,
    nested.threeMonthOfferAvailable
  );
  const customer_name = pickBillingString(
    o.customer_name,
    nested.customer_name,
    o.customerName,
    nested.customerName,
    customer?.name,
    paymentMethod?.billing_details &&
      typeof paymentMethod.billing_details === "object" &&
      (paymentMethod.billing_details as Record<string, unknown>).name
  );
  const card_brand = pickBillingString(
    o.card_brand,
    nested.card_brand,
    o.cardBrand,
    nested.cardBrand,
    pmCard?.brand
  );
  const card_last4 = pickBillingString(
    o.card_last4,
    nested.card_last4,
    o.cardLast4,
    nested.cardLast4,
    pmCard?.last4
  );
  const expMonthRaw =
    o.card_exp_month ??
    nested.card_exp_month ??
    o.cardExpMonth ??
    nested.cardExpMonth ??
    pmCard?.exp_month;
  const expYearRaw =
    o.card_exp_year ??
    nested.card_exp_year ??
    o.cardExpYear ??
    nested.cardExpYear ??
    pmCard?.exp_year;
  const card_exp_month =
    expMonthRaw == null ? null : Number.isFinite(Number(expMonthRaw)) ? Number(expMonthRaw) : null;
  const card_exp_year =
    expYearRaw == null ? null : Number.isFinite(Number(expYearRaw)) ? Number(expYearRaw) : null;
  const obj = <T,>(v: unknown): T | undefined =>
    v && typeof v === "object" ? (v as T) : undefined;
  const effectiveRaw = String(o.effective_plan ?? nested.effective_plan ?? "").toLowerCase().trim();
  const effective_plan: EffectivePlan | undefined =
    effectiveRaw === "free" ? "free" : normalizeBillingPlan(effectiveRaw) ?? undefined;
  const entitlements = obj<PlanEntitlements>(o.entitlements ?? nested.entitlements);
  const ai = obj<AiBalance>(o.ai ?? nested.ai);
  const backtests = obj<BacktestAllowance>(o.backtests ?? nested.backtests);
  const packs = obj<PackOffer>(o.packs ?? nested.packs);
  const addons = obj<Record<string, number>>(o.addons ?? nested.addons);
  const intervalRaw = String(o.billing_interval ?? nested.billing_interval ?? "").toLowerCase();
  const billing_interval = intervalRaw === "month" || intervalRaw === "year" ? intervalRaw : undefined;

  return {
    status,
    ...(trial_end !== undefined ? { trial_end } : {}),
    ...(current_period_end !== undefined ? { current_period_end } : {}),
    ...(cancel_at_period_end !== undefined ? { cancel_at_period_end } : {}),
    ...(tier !== undefined && tier !== null ? { tier } : {}),
    ...(plan != null ? { plan } : {}),
    ...(reports_per_week !== undefined ? { reports_per_week } : {}),
    ...(sims_per_report !== undefined ? { sims_per_report } : {}),
    ...(reports_used_this_week !== undefined ? { reports_used_this_week } : {}),
    ...(three_month_offer_available !== undefined ? { three_month_offer_available } : {}),
    ...(customer_name !== undefined ? { customer_name } : {}),
    ...(card_brand !== undefined ? { card_brand } : {}),
    ...(card_last4 !== undefined ? { card_last4 } : {}),
    ...(card_exp_month !== null ? { card_exp_month } : {}),
    ...(card_exp_year !== null ? { card_exp_year } : {}),
    ...(effective_plan ? { effective_plan } : {}),
    ...(entitlements ? { entitlements } : {}),
    ...(ai ? { ai } : {}),
    ...(backtests ? { backtests } : {}),
    ...(packs ? { packs } : {}),
    ...(addons ? { addons } : {}),
    ...(billing_interval ? { billing_interval } : {}),
  };
}

export interface BillingCheckoutResponse {
  url: string;
}

export interface BillingPortalResponse {
  url: string;
}

/**
 * Coarse UI state for messaging (maps from raw `status` + business rules).
 */
export type BillingUiState =
  | "loading"
  | "unknown"
  | "unsubscribed"
  | "trialing"
  | "active"
  | "past_due"
  | "canceled"
  | "requires_action";

export function deriveBillingUiState(
  status: BillingSubscriptionStatus | null | undefined
): Exclude<BillingUiState, "loading"> {
  if (status == null || status === "") return "unknown";
  const s = String(status).toLowerCase();
  if (s === "trialing") return "trialing";
  if (s === "active") return "active";
  if (s === "past_due" || s === "pastdue") return "past_due";
  if (s === "canceled" || s === "cancelled") return "canceled";
  if (s === "incomplete" || s === "unpaid" || s === "incomplete_expired") {
    return "requires_action";
  }
  return "unsubscribed";
}

/** Paid if `status === "active"` (or `"trialing"` on old rows). Use GET /billing/status, not `in_trial`. */
export function isTier1AccessGranted(status: BillingSubscriptionStatus | null | undefined): boolean {
  if (status == null) return false;
  const s = String(status).toLowerCase().trim();
  return s === "trialing" || s === "active" || s === "trial";
}

export function parseBillingTimestamp(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Paid access from GET /billing/status.
 * All subscriptions (including the 3-month coupon offer) are real Stripe subscriptions.
 */
export function isBillingAccessGranted(
  status: BillingStatusResponse | null | undefined,
  _nowMs?: number
): boolean {
  if (!status || !isTier1AccessGranted(status.status)) return false;
  return true;
}

/** Show the 3-month offer tab only when the server says it is available. */
export function shouldOfferThreeMonthCheckout(
  status: BillingStatusResponse | null | undefined,
): boolean {
  return isThreeMonthOfferAvailable(status);
}

export function resolveBillingPlan(status: BillingStatusResponse | null | undefined): BillingPlan | null {
  if (!status) return null;
  return status.plan ?? normalizeBillingPlan(status.tier);
}

/** Active sims allowed per report. Defaults to Standard (5) when unknown. */
export function resolveSimsPerReport(status: BillingStatusResponse | null | undefined): number {
  const n = status?.sims_per_report;
  if (typeof n === "number" && Number.isFinite(n) && n > 0) return Math.floor(n);
  return resolveBillingPlan(status) === "plus" ? PLUS_SIMS_PER_REPORT : STANDARD_SIMS_PER_REPORT;
}

export function resolveReportsPerWeek(status: BillingStatusResponse | null | undefined): number | null {
  const n = status?.reports_per_week;
  if (typeof n === "number" && Number.isFinite(n) && n > 0) return Math.floor(n);
  const plan = resolveBillingPlan(status);
  if (plan === "plus") return PLUS_REPORTS_PER_WEEK;
  if (plan === "standard") return STANDARD_REPORTS_PER_WEEK;
  return null;
}

export function isWeeklyReportQuotaReached(status: BillingStatusResponse | null | undefined): boolean {
  const used = status?.reports_used_this_week;
  const cap = resolveReportsPerWeek(status);
  if (used == null || cap == null || !Number.isFinite(used)) return false;
  return used >= cap;
}

export function isThreeMonthOfferAvailable(status: BillingStatusResponse | null | undefined): boolean {
  return status?.three_month_offer_available === true;
}

/**
 * Paywall launch pricing mirrors the marketing site for unsubscribed users
 * (banner + intro amounts on Monthly). Checkout uses the 3-month Stripe interval
 * while the offer is live.
 */
export function shouldShowPaywallLaunchPricing(
  status: BillingStatusResponse | null | undefined,
  offerEndedLocally = false,
): boolean {
  if (offerEndedLocally) return false;
  return !isBillingAccessGranted(status);
}

export function formatWeeklyReportQuotaMessage(
  status: BillingStatusResponse | null | undefined
): string {
  const used = status?.reports_used_this_week;
  const cap = resolveReportsPerWeek(status);
  const plan = resolveBillingPlan(status);
  const upgradeHint =
    plan === "plus"
      ? "Wait until next week."
      : "Wait until next week, or upgrade to Plus for a higher cap.";
  if (used != null && cap != null) {
    return `You've used ${used} of ${cap} reports this week. ${upgradeHint}`;
  }
  return `You've reached this week's report limit. ${upgradeHint}`;
}

/**
 * Primary checkout CTA: POST /billing/checkout has no trial flag — Stripe defines terms.
 * Use "Start trial" only when status looks like a clean no-subscription state and we have no local hint
 * the user already opened Checkout (after that, prefer "Subscribe").
 */
export function tier1CheckoutCtaLabel(
  status: BillingStatusResponse | null,
  hasAttemptedCheckoutLocally: boolean
): "Start trial" | "Subscribe" {
  if (hasAttemptedCheckoutLocally) return "Subscribe";
  if (status == null) return "Subscribe";

  const raw = status.status;
  if (raw == null) return "Start trial";
  if (typeof raw === "string" && raw.trim() === "") return "Start trial";

  const s = String(raw).toLowerCase();
  if (s === "none" || s === "null") return "Start trial";

  const ui = deriveBillingUiState(raw);
  if (ui === "unsubscribed" || ui === "unknown") return "Start trial";

  return "Subscribe";
}


/** Whether the plan in effect opens ``feature``. Unknown / loading counts as not open. */
export function planHasFeature(
  status: BillingStatusResponse | null | undefined,
  feature: PlanFeature
): boolean {
  const features = status?.entitlements?.features;
  if (Array.isArray(features)) return features.includes(feature);
  // Older backend without entitlements: paid access opened everything.
  return isBillingAccessGranted(status);
}

export function effectivePlanOf(status: BillingStatusResponse | null | undefined): EffectivePlan {
  if (status?.effective_plan) return status.effective_plan;
  if (!isBillingAccessGranted(status)) return "free";
  return status?.plan ?? "standard";
}
